import { Box, Flex, IconButton, Text, VStack } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { FunctionComponent } from 'react';
import { FILE_UPLOAD_STATUS } from '@/constants';
import { InProgress } from '@/components/Status';
import { SelectedUploadFile } from '@/types/upload';

type Props = {
  files: SelectedUploadFile[];
  onRemove?: (fileName: string) => void;
  isRemoveDisabled?: boolean;
  showProgress?: boolean;
  ariaLabel?: string;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileList: FunctionComponent<Props> = ({
  files,
  onRemove,
  isRemoveDisabled = false,
  showProgress = false,
  ariaLabel = 'Selected files',
}) => {
  if (files.length === 0) return null;

  return (
    <VStack as="ul" spacing={2} align="stretch" listStyleType="none" m={0} p={0} aria-label={ariaLabel}>
      {files.map(({ file, progress, status, error }) => (
        <Box
          as="li"
          key={file.name}
          border="1px solid"
          borderColor={status === FILE_UPLOAD_STATUS.ERROR ? 'red.300' : 'marketcast.gray.5'}
          borderRadius="8px"
          p={3}
        >
          <Flex justify="space-between" align="center" gap={3}>
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="500" color="marketcast.text.primary" noOfLines={1} title={file.name}>
                {file.name}
              </Text>
              <Text fontSize="xs" color="marketcast.gray.2">
                {formatFileSize(file.size)}
              </Text>
            </Box>
            {/* Uploaded files stay removable so a slot can be freed; the S3 copy is kept
                (and overwritten if a file with the same name is uploaded again). */}
            {onRemove && !isRemoveDisabled && status !== FILE_UPLOAD_STATUS.UPLOADING && (
              <IconButton
                aria-label={`Remove ${file.name}`}
                icon={<X size={16} />}
                size="sm"
                variant="ghost"
                onClick={() => onRemove(file.name)}
              />
            )}
          </Flex>

          {showProgress && status === FILE_UPLOAD_STATUS.PENDING && (
            <Text mt={2} fontSize="xs" color="marketcast.gray.2">
              Ready to upload
            </Text>
          )}
          {showProgress && (status === FILE_UPLOAD_STATUS.UPLOADING || status === FILE_UPLOAD_STATUS.SUCCESS) && (
            <Box mt={2} aria-live="polite">
              <InProgress currentProgress={progress} />
            </Box>
          )}
          {showProgress && status === FILE_UPLOAD_STATUS.ERROR && (
            <Text mt={2} fontSize="xs" color="red.500" role="alert">
              {error}
            </Text>
          )}
        </Box>
      ))}
    </VStack>
  );
};
